import type { SaldoFormData } from "./saldoSchema";
import type { PreviewNotificada, PreviewResponse } from "../types/preview";
import { formatBRL, formatISODateBR } from "./format";
import { maskCpfCnpj, parseBRL } from "./masks";
import { generateProtocol, obfuscateText } from "./mocks";
import { evaluateDeadline } from "./deadline";
import { CNPJ_A_CONFIRMAR, entryEmails, notificadaFromEntry } from "./registry";
import type { LocalRegistryEntry } from "./registry";

const DEFAULT_ENDERECAMENTO = "Departamento Jurídico, Compliance e Ouvidoria";
export const SALDO_TITULO = "NOTIFICAÇÃO EXTRAJUDICIAL DE LIBERAÇÃO DE SALDO RETIDO";

// Espelho local do mapeamento do servidor — usado apenas no fallback offline. CNPJ null = informado inválido.
export const SALDO_REGISTRY: Record<string, LocalRegistryEntry> = {
  "Mercado Pago": { razaoSocial: "Mercado Pago Instituição de Pagamento Ltda.", cnpj: "10.573.521/0001-91", emails: ["ouvidoria@mercadopago.com.br", "resolucoes@mercadopago.com.br"], sede: "Osasco/SP" },
  PagBank: { razaoSocial: "PagBank Internet Instituição de Pagamento S.A.", cnpj: "08.561.701/0001-01", emails: ["ouvidoria@pagseguro.com", "atendimento@pagseguro.com"], sede: "São Paulo/SP" },
  ShopeePay: { razaoSocial: "SHPP Brasil Instituição de Pagamento e Serviços de Pagamentos Ltda", cnpj: "38.372.267/0001-82", emails: ["ouvidoria@shopee.com", "ajuda@suporte.shopee.com.br"], sede: "São Paulo/SP" },
  Stripe: { razaoSocial: "Stripe Brasil Soluções de Pagamento Ltda.", cnpj: "22.121.209/0001-46", emails: ["ouvidoria-brasil@stripe.com", "suporte@stripe.com"], sede: "São Paulo/SP" },
  "Stone/Ton": { razaoSocial: "Stone Instituição de Pagamento S.A.", cnpj: "16.501.555/0001-57", emails: ["ouvidoria@stone.com.br", "relacionamento@stone.com.br"], sede: "São Paulo/SP" },
  InfinitePay: { razaoSocial: "Cloudwalk Instituição de Pagamentos e Serviços Ltda", cnpj: "18.189.547/0001-42", emails: ["ouvidoria@cloudwalk.io", "ajuda@infinitepay.io"], sede: "São Paulo/SP" },
  "Asaas Gestão Financeira": { razaoSocial: "Asaas Gestão Financeira S.A.", cnpj: "19.540.550/0001-21", emails: ["ouvidoria@asaas.com.br", "suporte@asaas.com.br"], sede: "Joinville/SC" },
};
const UNVERIFIED: Record<string, string> = {};

function resolveNotificada(data: SaldoFormData): PreviewNotificada {
  const entry = SALDO_REGISTRY[data.instituicao];
  if (entry) return notificadaFromEntry(entry, DEFAULT_ENDERECAMENTO);
  return { razaoSocial: UNVERIFIED[data.instituicao] ?? data.instituicaoOutra.trim(), cnpj: CNPJ_A_CONFIRMAR, verificado: false, enderecamento: DEFAULT_ENDERECAMENTO, sede: null };
}

function buildBody(data: SaldoFormData, notificada: string, doc: string, days: number): string[] {
  const valor = formatBRL(parseBRL(data.valorBloqueado));
  const tipo = data.tipoTitular === "PF" ? "CPF" : "CNPJ";
  return [
    `${data.razaoSocialOuNome.trim()}, inscrito(a) no ${tipo} sob o nº ${doc}, vem NOTIFICAR EXTRAJUDICIALMENTE ${notificada} acerca da retenção de ${valor}, bloqueados desde ${formatISODateBR(data.dataBloqueio)} (${days} dias), em afronta ao dever de liquidação previsto nos Arts. 6º e 9º da Lei nº 12.865/2013.`,
    "A Resolução BCB nº 150/2021 e as normas correlatas do Banco Central impõem liquidação tempestiva e transparência, e a retenção prolongada configura enriquecimento sem causa vedado pelo art. 884 do Código Civil, com risco de colapso operacional do titular.",
    `Requer-se a liberação integral de ${valor} no prazo improrrogável de 48 horas úteis, sob pena de reclamação no RDR do Banco Central, no Procon e de Ação Cominatória com pedido de tutela de urgência e astreintes diárias.`,
  ];
}

export function buildLocalSaldoPreview(data: SaldoFormData): PreviewResponse {
  const notificada = resolveNotificada(data);
  const doc = maskCpfCnpj(data.documentoTitular);
  const days = Math.max(0, evaluateDeadline(data.dataBloqueio).days);
  return {
    protocolo: generateProtocol(),
    emitidoEm: new Date().toISOString(),
    diasDecorridos: days,
    source: "mock",
    notificada,
    notificante: { nome: data.razaoSocialOuNome.trim(), cpfFormatado: doc, documentoTipo: data.tipoTitular === "PF" ? "CPF" : "CNPJ", documentoFormatado: doc, conta: data.identificadorConta.trim() },
    emails: entryEmails(SALDO_REGISTRY[data.instituicao]),
    paragrafosOfuscados: buildBody(data, notificada.razaoSocial, doc, days).map(obfuscateText),
    niche: "saldo-retido",
    tituloDocumento: SALDO_TITULO,
    destaque: { valorRetido: parseBRL(data.valorBloqueado), dataBloqueio: data.dataBloqueio, diasRetencao: days },
  };
}
